import React, { useEffect, useState } from 'react'
import {
    Alert, Button, Checkbox, Col, Collapse, Descriptions, Drawer, Form, Input,
    message, Row, Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { EditOutlined, HistoryOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, ToolOutlined } from '@ant-design/icons'
import {
    createAppBuildProject, detectAppBuildProject, getAppBuildProjectConfigs, updateAppBuildProject,
} from '../../request'
import AppBuildAgentRegistration from './AppBuildAgentRegistration'

const { Option } = Select
const { Panel } = Collapse
const { Text } = Typography
const { TextArea } = Input

const ENVIRONMENTS = [
    { key: 'test', name: '测试环境', rule: '稳定版、beta/rc 或 test' },
    { key: 'preview', name: '预发布环境', rule: '稳定版或 beta/rc' },
    { key: 'production', name: '正式环境', rule: 'vX.Y.Z' },
]

const TARGETS = [
    { key: 'mac-arm64', name: 'macOS Apple Silicon', platform: 'macos', architecture: 'arm64', executor: 'macos-native' },
    { key: 'mac-x64', name: 'macOS Intel', platform: 'macos', architecture: 'x64', executor: 'macos-native' },
    { key: 'windows-x64', name: 'Windows x64', platform: 'windows', architecture: 'x64', executor: 'windows-cross' },
    { key: 'linux-x64', name: 'Linux x64', platform: 'linux', architecture: 'x64', executor: 'linux-docker' },
    { key: 'linux-arm64', name: 'Linux ARM64', platform: 'linux', architecture: 'arm64', executor: 'linux-docker' },
]

const splitLines = value => String(value || '').split(/[\n,]/).map(item => item.trim()).filter(Boolean)

const canonicalEnvironment = key => {
    if (['staging', 'pre', 'preproduction'].includes(key)) return 'preview'
    if (key === 'prod') return 'production'
    return key
}

const executorForTarget = (target, templateKey) => (
    target.platform === 'windows' && templateKey === 'tauri-desktop' ? 'windows-vm' : target.executor
)

const defaultConfig = detection => {
    const templateKey = detection.recommendedTemplate || 'electron-desktop'
    return {
        applicationDirectory: detection.recommendedApplicationDirectory || '.',
        installDirectory: '.',
        buildDirectory: detection.recommendedApplicationDirectory || '.',
        packageManager: detection.packageManager || 'npm',
        installMode: 'frozen',
        buildScript: templateKey === 'electron-desktop' ? detection.recommendedBuildScript : undefined,
        tauriScript: templateKey === 'tauri-desktop' ? (detection.recommendedBuildScript || 'tauri') : undefined,
        workspaceFilter: undefined,
        artifactGlobsText: templateKey === 'tauri-desktop'
            ? 'src-tauri/target/release/bundle/**/*'
            : 'dist/**/*',
        allowedEnvironmentKeysText: '',
    }
}

const toFormValue = project => {
    if (!project) return {
        enabled: true,
        environmentKeys: ['production'],
        targetKeys: ['mac-arm64'],
        config: defaultConfig({}),
        profileScripts: {},
    }
    const scripts = (project.config && project.config.profileBuildScripts) || {}
    const normalizedScripts = Object.keys(scripts).reduce((result, key) => ({
        ...result,
        [canonicalEnvironment(key)]: scripts[key],
    }), {})
    return {
        ...project,
        repositoryUrl: `https://github.com/${project.repository}`,
        environmentKeys: (project.profiles || []).map(item => canonicalEnvironment(item.profileKey)),
        targetKeys: (project.targets || []).map(item => item.targetKey),
        profileScripts: normalizedScripts,
        config: {
            ...(project.config || {}),
            artifactGlobsText: ((project.config && project.config.artifactGlobs) || []).join('\n'),
            allowedEnvironmentKeysText: ((project.config && project.config.allowedEnvironmentKeys) || []).join('\n'),
        },
    }
}

const toPayload = (values, detection, originalProject) => {
    const templateKey = values.templateKey
    const originalProfiles = (originalProject && originalProject.profiles) || []
    const profiles = (values.environmentKeys || []).map((key, index) => {
        const definition = ENVIRONMENTS.find(item => item.key === key)
        const original = originalProfiles.find(item => canonicalEnvironment(item.profileKey) === key)
        return {
            profileKey: key,
            profileName: definition.name,
            environment: (original && original.environment) || {},
            enabled: true,
            sortNo: (index + 1) * 10,
        }
    })
    const profileBuildScripts = {}
    Object.keys(values.profileScripts || {}).forEach(key => {
        const script = String(values.profileScripts[key] || '').trim()
        if (script && (values.environmentKeys || []).includes(key)) profileBuildScripts[key] = script
    })
    const targets = (values.targetKeys || []).map((key, index) => {
        const target = TARGETS.find(item => item.key === key)
        return {
            targetKey: key,
            targetName: target.name,
            platform: target.platform,
            architecture: target.architecture,
            executorKey: executorForTarget(target, templateKey),
            enabled: true,
            sortNo: (index + 1) * 10,
        }
    })
    return {
        projectKey: values.projectKey.trim(),
        projectName: values.projectName.trim(),
        repositoryOwner: values.repositoryOwner.trim(),
        repositoryName: values.repositoryName.trim(),
        defaultBranch: values.defaultBranch.trim(),
        templateKey,
        detectionSnapshot: detection || values.detectionSnapshot || {},
        enabled: values.enabled !== false,
        sortNo: values.sortNo || 0,
        config: {
            ...values.config,
            applicationDirectory: values.config.applicationDirectory.trim(),
            installDirectory: values.config.installDirectory.trim(),
            buildDirectory: values.config.buildDirectory.trim(),
            buildScript: values.config.buildScript ? values.config.buildScript.trim() : undefined,
            tauriScript: values.config.tauriScript ? values.config.tauriScript.trim() : undefined,
            workspaceFilter: values.config.workspaceFilter ? values.config.workspaceFilter.trim() : undefined,
            profileBuildScripts,
            artifactGlobs: splitLines(values.config.artifactGlobsText),
            allowedEnvironmentKeys: splitLines(values.config.allowedEnvironmentKeysText),
        },
        profiles,
        targets,
    }
}

export default function AppBuildProjectConfig({ onBuild, onHistory, onProjectsChanged }) {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [projects, setProjects] = useState([])
    const [visible, setVisible] = useState(false)
    const [editingProject, setEditingProject] = useState(null)
    const [saving, setSaving] = useState(false)
    const [detecting, setDetecting] = useState(false)
    const [detection, setDetection] = useState(null)
    const environmentKeys = Form.useWatch('environmentKeys', form) || []
    const templateKey = Form.useWatch('templateKey', form) || 'electron-desktop'
    const projectKey = Form.useWatch('projectKey', form)
    const projectName = Form.useWatch('projectName', form)
    const defaultBranch = Form.useWatch('defaultBranch', form)
    const applicationDirectory = Form.useWatch(['config', 'applicationDirectory'], form)
    const packageManager = Form.useWatch(['config', 'packageManager'], form)

    const load = () => {
        setLoading(true)
        return getAppBuildProjectConfigs().then(data => setProjects(data || []))
            .catch(() => {})
            .finally(() => setLoading(false))
    }

    useEffect(() => { load() }, [])

    const open = project => {
        const initial = toFormValue(project)
        form.resetFields()
        setEditingProject(project || null)
        setDetection(project ? project.detectionSnapshot : null)
        setVisible(true)
        setTimeout(() => form.setFieldsValue(initial), 0)
    }

    const detect = () => form.validateFields(['repositoryUrl']).then(values => {
        setDetecting(true)
        return detectAppBuildProject({ repositoryUrl: values.repositoryUrl.trim() }).then(result => {
            setDetection(result)
            form.setFieldsValue({
                projectKey: result.projectKey,
                projectName: result.projectName,
                repositoryOwner: result.repositoryOwner,
                repositoryName: result.repositoryName,
                defaultBranch: result.defaultBranch,
                templateKey: result.recommendedTemplate,
                config: defaultConfig(result),
            })
            message.success('仓库检测完成，项目名称、默认分支和构建模板已自动填写')
        }).finally(() => setDetecting(false))
    })

    const save = () => form.validateFields().then(values => {
        if (!detection && !editingProject) {
            message.error('请先检测 GitHub 仓库')
            return Promise.reject(new Error('repository not detected'))
        }
        const payload = toPayload(values, detection, editingProject)
        setSaving(true)
        const request = editingProject
            ? updateAppBuildProject(editingProject.projectKey, payload)
            : createAppBuildProject(payload)
        return request.then(() => {
            message.success(editingProject ? '项目配置已更新' : '项目已创建')
            setVisible(false)
            load()
            if (onProjectsChanged) onProjectsChanged()
        }).finally(() => setSaving(false))
    }).catch(() => {})

    const columns = [
        { title: '项目', dataIndex: 'projectName', width: 180, render: (value, row) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{row.projectKey}</Text></Space> },
        { title: 'GitHub 仓库', dataIndex: 'repository', width: 230, render: value => <a href={`https://github.com/${value}`} target="_blank" rel="noopener noreferrer">{value}</a> },
        { title: '类型', dataIndex: 'templateKey', width: 100, render: value => <Tag color={value === 'electron-desktop' ? 'blue' : 'purple'}>{value === 'electron-desktop' ? 'Electron' : 'Tauri'}</Tag> },
        { title: '环境', dataIndex: 'profiles', width: 170, render: value => (value || []).filter(item => item.enabled !== false).map(item => <Tag key={item.profileKey}>{item.profileName}</Tag>) },
        { title: '平台', dataIndex: 'targets', width: 240, render: value => (value || []).filter(item => item.enabled !== false).map(item => <Tag key={item.targetKey}>{item.targetName}</Tag>) },
        { title: '状态', dataIndex: 'enabled', width: 80, render: value => <Tag color={value ? 'success' : 'default'}>{value ? '启用' : '停用'}</Tag> },
        { title: '操作', key: 'action', width: 260, fixed: 'right', render: (_, row) => <div className="app-builder-project-actions">
            <Button type="link" disabled={!row.enabled} onClick={() => onBuild(row)}>构建</Button>
            <Button type="link" icon={<HistoryOutlined />} onClick={() => onHistory(row)}>任务记录</Button>
            <Button type="link" icon={<EditOutlined />} onClick={() => open(row)}>编辑</Button>
        </div> },
    ]

    return <>
        <div className="app-builder-toolbar">
            <Space wrap><AppBuildAgentRegistration /><Button icon={<ReloadOutlined />} onClick={load}>刷新</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>新增项目</Button></Space>
        </div>
        <Table rowKey="projectKey" loading={loading} dataSource={projects} columns={columns} pagination={false} scroll={{ x: 1260 }} />
        <Drawer
            title={editingProject ? `编辑项目 · ${editingProject.projectName}` : '新增构建项目'}
            visible={visible}
            width={720}
            destroyOnClose
            onClose={() => setVisible(false)}
            footer={<div className="app-builder-drawer-footer"><Button onClick={() => setVisible(false)}>取消</Button><Button type="primary" loading={saving} onClick={save}>保存项目</Button></div>}
        >
            <Form form={form} layout="vertical">
                <Form.Item name="repositoryUrl" label="GitHub 仓库地址" rules={[{ required: true, whitespace: true, message: '请输入 GitHub 仓库地址' }]}>
                    <Input.Search enterButton={<><SearchOutlined /> 检测项目</>} loading={detecting} onSearch={detect} disabled={!!editingProject} placeholder="https://github.com/owner/repository" />
                </Form.Item>

                {projectKey ? <Descriptions bordered size="small" column={2} className="app-builder-detection-summary">
                    <Descriptions.Item label="项目名称">{projectName}</Descriptions.Item>
                    <Descriptions.Item label="项目标识">{projectKey}</Descriptions.Item>
                    <Descriptions.Item label="默认分支">{defaultBranch}</Descriptions.Item>
                    <Descriptions.Item label="构建类型">{templateKey === 'tauri-desktop' ? 'Tauri' : 'Electron'}</Descriptions.Item>
                    <Descriptions.Item label="应用目录">{applicationDirectory || '.'}</Descriptions.Item>
                    <Descriptions.Item label="包管理器">{packageManager || '-'}</Descriptions.Item>
                </Descriptions> : null}

                <Form.Item name="environmentKeys" label="可构建环境" rules={[{ required: true, message: '至少选择一个环境' }]}>
                    <Checkbox.Group className="app-builder-option-grid">
                        {ENVIRONMENTS.map(item => <Checkbox key={item.key} value={item.key}>
                            <span className="app-builder-option-title">{item.name}</span>
                            <span className="app-builder-option-help">Tag：{item.rule}</span>
                        </Checkbox>)}
                    </Checkbox.Group>
                </Form.Item>

                <Form.Item name="targetKeys" label="可构建平台" rules={[{ required: true, message: '至少选择一个平台' }]}>
                    <Checkbox.Group className="app-builder-option-grid">
                        {TARGETS.map(item => <Checkbox key={item.key} value={item.key}>{item.name}</Checkbox>)}
                    </Checkbox.Group>
                </Form.Item>

                <Collapse ghost className="app-builder-advanced">
                    <Panel forceRender header={<Space><ToolOutlined />高级构建设置<Text type="secondary">通常无需修改</Text></Space>} key="advanced">
                        <Alert type="warning" showIcon message="仅在自动识别结果不能满足项目构建命令时修改以下内容。" style={{ marginBottom: 16 }} />
                        <Row gutter={16}>
                            <Col span={12}><Form.Item name="projectName" label="项目名称" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="projectKey" label="项目标识" rules={[{ required: true }, { pattern: /^[a-z0-9][a-z0-9._-]{0,63}$/, message: '仅允许小写字母、数字、点、横线和下划线' }]}><Input disabled={!!editingProject} /></Form.Item></Col>
                            <Col span={12}><Form.Item name="defaultBranch" label="默认分支" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="templateKey" label="构建模板" rules={[{ required: true }]}><Select><Option value="electron-desktop">Electron</Option><Option value="tauri-desktop">Tauri</Option></Select></Form.Item></Col>
                            <Col span={8}><Form.Item name={['config', 'applicationDirectory']} label="应用目录" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={8}><Form.Item name={['config', 'installDirectory']} label="依赖安装目录" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={8}><Form.Item name={['config', 'buildDirectory']} label="构建目录" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name={['config', 'packageManager']} label="包管理器" rules={[{ required: true }]}><Select><Option value="npm">npm</Option><Option value="pnpm">pnpm</Option><Option value="yarn">yarn</Option></Select></Form.Item></Col>
                            <Col span={12}>{templateKey === 'tauri-desktop'
                                ? <Form.Item name={['config', 'tauriScript']} label="Tauri 构建脚本" rules={[{ required: true }]}><Input /></Form.Item>
                                : <Form.Item name={['config', 'buildScript']} label="Electron 通用构建脚本"><Input /></Form.Item>}
                            </Col>
                            {templateKey === 'electron-desktop' && environmentKeys.map(key => {
                                const env = ENVIRONMENTS.find(item => item.key === key)
                                return <Col span={12} key={key}><Form.Item name={['profileScripts', key]} label={`${env.name}构建脚本（可选）`}><Input placeholder="留空则使用通用构建脚本" /></Form.Item></Col>
                            })}
                            <Col span={24}><Form.Item name={['config', 'artifactGlobsText']} label="产物路径（每行一个）" rules={[{ required: true }]}><TextArea rows={3} /></Form.Item></Col>
                        </Row>
                        <Form.Item name="repositoryOwner" hidden><Input /></Form.Item>
                        <Form.Item name="repositoryName" hidden><Input /></Form.Item>
                        <Form.Item name="enabled" valuePropName="checked" label="项目状态"><Switch checkedChildren="启用" unCheckedChildren="停用" /></Form.Item>
                    </Panel>
                </Collapse>
            </Form>
        </Drawer>
    </>
}
